import { useEffect, useRef } from "react";
import { Bot, Headset } from "lucide-react";

const time = (iso) => new Date(iso).toLocaleTimeString("sv-SE", { hour: "2-digit", minute: "2-digit" });

const rich = (text) =>
  text.split(/(\*\*[^*]+\*\*|https?:\/\/[^\s)]+)/g).map((part, i) => {
    if (part.startsWith("**")) return <strong key={i}>{part.slice(2, -2)}</strong>;
    if (part.startsWith("http")) return <a key={i} href={part} target="_blank" rel="noopener noreferrer" className="underline font-semibold break-all">{part.replace(/^https?:\/\//, "")}</a>;
    return part;
  });

export default function ChatMessages({ messages, streaming = "", waiting = false, viewer = "visitor" }) {
  const endRef = useRef(null);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, streaming, waiting]);

  const mine = (role) => (viewer === "visitor" ? role === "visitor" : role === "agent");

  return (
    <div className="flex-1 overflow-y-auto px-3 sm:px-4 py-3 sm:py-4 space-y-2.5 bg-[#f6f9fc] chat-scroll" data-testid="chat-messages">
      {messages.map((m) => {
        if (m.role === "system") {
          return <p key={m.id} data-testid="chat-msg-system" className="text-center text-[11px] text-slate-400 px-4 py-1 leading-snug">{m.text}</p>;
        }
        const own = mine(m.role);
        return (
          <div key={m.id} data-testid={`chat-msg-${m.role}`} className={`flex items-end gap-2 ${own ? "justify-end" : "justify-start"}`}>
            {!own && (
              <span className={`w-6 h-6 sm:w-7 sm:h-7 rounded-full flex items-center justify-center flex-shrink-0 ${m.role === "ai" ? "bg-[#ffce3a] text-[#0B2545]" : m.role === "agent" ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"}`}>
                {m.role === "ai" ? <Bot size={14} /> : m.role === "agent" ? <Headset size={14} /> : <span className="text-[10px] font-bold">B</span>}
              </span>
            )}
            <div className={`max-w-[86%] sm:max-w-[80%] min-w-0 px-3.5 py-2.5 text-[14px] leading-[1.5] whitespace-pre-wrap break-words shadow-sm ${own ? "bg-[#0B2545] text-white rounded-2xl rounded-br-md" : "bg-white text-slate-800 rounded-2xl rounded-bl-md border border-slate-100"}`}>
              {rich(m.text)}
              <span className={`block text-[10px] mt-1 opacity-80 ${own ? "text-slate-300" : "text-slate-400"}`}>{m.role === "ai" ? "AI · " : m.role === "agent" ? "Receptionen · " : ""}{time(m.created_at)}</span>
            </div>
          </div>
        );
      })}
      {streaming && (
        <div className="flex items-end gap-2" data-testid="chat-msg-streaming">
          <span className="w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-[#ffce3a] text-[#0B2545] flex items-center justify-center flex-shrink-0"><Bot size={14} /></span>
          <div className="max-w-[86%] sm:max-w-[80%] min-w-0 px-3.5 py-2.5 text-[14px] leading-[1.5] whitespace-pre-wrap break-words bg-white text-slate-800 rounded-2xl rounded-bl-md border border-slate-100 shadow-sm">
            {streaming.trim() ? rich(streaming) : <span className="inline-flex gap-1"><i className="chat-dot" /><i className="chat-dot" style={{ animationDelay: "0.15s" }} /><i className="chat-dot" style={{ animationDelay: "0.3s" }} /></span>}
          </div>
        </div>
      )}
      {waiting && !streaming && (
        <p className="text-center text-[11px] text-slate-400 flex items-center justify-center gap-1.5" data-testid="chat-waiting-note"><span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> Receptionen har fått ditt meddelande och svarar strax…</p>
      )}
      <div ref={endRef} />
    </div>
  );
}
